import { type Dispatch, type SetStateAction, useCallback, useState } from "react";

/**
 * A `Set<T>` of ids with a stable `toggle(id)` — the add/delete-on-a-copied-Set
 * pattern the v3 grids hand-rolled for every collapsed / expanded / strip-shown
 * membership state. Returns `[set, toggle, setSet]`; `setSet` is the raw setter
 * for bulk operations (select-all / clear-all).
 */
export const useToggleSet = <T>(initial?: Iterable<T>): [Set<T>, (id: T) => void, Dispatch<SetStateAction<Set<T>>>] => {
	const [set, setSet] = useState<Set<T>>(() => new Set(initial));
	const toggle = useCallback((id: T) => {
		setSet((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);
	return [set, toggle, setSet];
};
